import { useMemo, useState } from 'react';
import { Box, Button, Dialog, IconButton } from '@mui/material';
import CodeEditor from 'components/codeEditor/CodeEditor';
import { parseOutputCellContent } from 'utils/helpers';
import { useQuery } from '@tanstack/react-query';
import { QueryKey } from 'api/model';
import { getNodeCellOutputData } from 'api/workflows/requests';
import Loader from 'components/Loader';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import { useKeyboard } from 'hooks/useKeyboard';

export const CELL_PREVIEW_SIZE = 100;

export type CellData = {
  index: number;
  column: string;
  content: string;
};

type NodeOutputCellDialogProps = {
  workflowExecutionId: string;
  nodeId: string;
  cellData?: CellData;
  onClose: () => void;
  onValueSelect?: (value: string) => void;
};

const NodeOutputCellDialog = ({
  workflowExecutionId,
  nodeId,
  cellData,
  onClose,
  onValueSelect,
}: NodeOutputCellDialogProps) => {
  const isPreview = cellData && cellData.content.length > CELL_PREVIEW_SIZE && cellData.content.endsWith('…');
  const [isFullScreen, setIsFullScreen] = useState(false);

  const { data: fetchedCellValue, isLoading } = useQuery({
    queryKey: [QueryKey.NodeOutputData, workflowExecutionId, nodeId, cellData?.index, cellData?.column],
    queryFn: () =>
      getNodeCellOutputData({
        workflowExecutionId,
        nodeId,
        index: cellData!.index,
        column: cellData!.column,
      }),
    enabled: isPreview,
  });

  const dialogContent = useMemo(() => {
    if (!cellData) return undefined;
    const content = fetchedCellValue ?? cellData.content;
    return parseOutputCellContent(content.toString());
  }, [cellData, fetchedCellValue]);

  const handleSelectValue = () => {
    if (onValueSelect && dialogContent?.content) {
      onValueSelect(dialogContent.content);
    }
    onClose();
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  useKeyboard({
    keyDown: (e) => {
      if (e.key === 'f') {
        toggleFullScreen();
      }
    },
  });

  return (
    <Dialog maxWidth="md" open={!!dialogContent} onClose={onClose} fullWidth fullScreen={isFullScreen}>
      <Box sx={{ backgroundColor: 'background.paper' }} className="flex flex-col h-full">
        <IconButton onClick={toggleFullScreen} aria-label="toggle-fullscreen">
          {isFullScreen ? <FullscreenExit /> : <Fullscreen />}
        </IconButton>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className={`${isFullScreen ? 'grow' : 'p-2'}`}>
              <CodeEditor
                height={isFullScreen ? '100%' : '60vh'}
                language={dialogContent?.language ?? 'plaintext'}
                value={dialogContent?.content ?? ''}
                readOnly
                key={`${isFullScreen}`}
              />
            </div>
            <div className="flex justify-end gap-2 p-2">
              {onValueSelect && (
                <Button onClick={handleSelectValue} color="primary" variant="contained">
                  Select
                </Button>
              )}
              <Button onClick={onClose} color="primary">
                Close
              </Button>
            </div>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default NodeOutputCellDialog;
